import { isFluxModel } from './flux-rules';
import {
  findLocaleOption,
  localeAvailability,
  localeOptions,
  type LanguageCatalogEntry,
  type LocaleUnavailableReason,
  type SpeechRecognition,
} from './language-catalog';
import type { LanguageEntry } from './languages-config';

export type LanguageEntryError =
  | 'no-voice'
  | 'no-audio-model'
  | 'unknown-locale'
  | 'duplicate-language'
  | Exclude<LocaleUnavailableReason, 'language-taken'>;

export type LanguagesFormError =
  | 'no-languages'
  | 'no-starting-language'
  | 'catalog-unavailable';

export type LanguagesValidation = {
  isValid: boolean;
  /** Parallel to `languages`: the problems of each entry, in order. */
  entryErrors: LanguageEntryError[][];
  formErrors: LanguagesFormError[];
  /** The one line shown next to Save, or null when there is nothing to fix. */
  summary: string | null;
};

const FORM_ERROR_SUMMARY: Record<LanguagesFormError, string> = {
  'no-languages': 'Add at least one language.',
  'no-starting-language': 'Select a starting language.',
  'catalog-unavailable':
    "The supported languages couldn't be loaded, so these languages can't be checked. Retry loading them before saving.",
};

/**
 * Validates the Languages block before save.
 *
 * This is the only guard there is. Engagement validates the overlay at call
 * time, not when Prime stores it, and a rejected overlay is not an error
 * anywhere the user can see: the call silently runs on the default language
 * alone. So every rule Engagement enforces is mirrored here (PRD §3–4).
 *
 * Locales are checked against the catalog once it has loaded. The English-only
 * model rule needs no catalog and always applies. Without the catalog the
 * caller decides, through `catalogUnavailable`, whether an overlay may still
 * be saved unchecked — an untouched channel should stay saveable, an edited
 * one should not.
 */
export const validateLanguages = ({
  languages,
  startingLanguage,
  speechRecognition,
  catalog,
  catalogUnavailable = false,
}: {
  languages: LanguageEntry[];
  startingLanguage: string;
  speechRecognition: SpeechRecognition;
  catalog: LanguageCatalogEntry[];
  /** The catalog is missing and the languages or speech recognition changed. */
  catalogUnavailable?: boolean;
}): LanguagesValidation => {
  const options = localeOptions(catalog);
  const keyCounts = new Map<string, number>();
  languages.forEach(({ key }) =>
    keyCounts.set(key, (keyCounts.get(key) || 0) + 1)
  );

  const entryErrors = languages.map((entry) => {
    const errors: LanguageEntryError[] = [];

    if ((keyCounts.get(entry.key) || 0) > 1) errors.push('duplicate-language');

    // Deepgram models other than Flux are English only. Known without the
    // catalog, so checked even when it is missing.
    const englishOnly =
      speechRecognition.transcriptionProvider === 'Deepgram' &&
      !isFluxModel(speechRecognition.speechModel);
    if (englishOnly && entry.key !== 'en') {
      errors.push('english-only-model');
    } else if (options.length > 0) {
      const option = findLocaleOption(options, catalog, entry.locale);
      if (!option) {
        errors.push('unknown-locale');
      } else {
        const availability = localeAvailability(option, speechRecognition, []);
        if (!availability.enabled && availability.reason !== 'language-taken') {
          errors.push(availability.reason);
        }
      }
    }

    if (!entry.elevenlabsAudioModel) errors.push('no-audio-model');
    if (!entry.elevenlabsVoiceId) errors.push('no-voice');

    return errors;
  });

  const formErrors: LanguagesFormError[] = [];
  if (languages.length === 0) {
    formErrors.push('no-languages');
  } else if (!languages.some(({ key }) => key === startingLanguage)) {
    formErrors.push('no-starting-language');
  }
  // A single language sends no overlay, so there is nothing to reject.
  if (catalogUnavailable && languages.length > 1) {
    formErrors.push('catalog-unavailable');
  }

  const invalidEntries = entryErrors.filter((errors) => errors.length).length;
  const summary =
    invalidEntries === 1
      ? '1 language entry has an unsupported configuration. Please resolve before saving.'
      : invalidEntries > 1
        ? `${invalidEntries} language entries have unsupported configurations. Please resolve before saving.`
        : formErrors.length
          ? FORM_ERROR_SUMMARY[formErrors[0]]
          : null;

  return {
    isValid: invalidEntries === 0 && formErrors.length === 0,
    entryErrors,
    formErrors,
    summary,
  };
};
